import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import "./SignUp.css";
import { IoInformationCircleOutline } from "react-icons/io5";
import { IoEyeOutline } from "react-icons/io5";
import { IoEyeOffOutline } from "react-icons/io5";
import config from "../../config";

const Signup = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [activeTab, setActiveTab] = useState("signup");
  const navigate = useNavigate(); // useNavigate from react-router
  const [showPopup, setShowPopup] = useState(false); // State to toggle the popup visibility
  const [errorMessage, setErrorMessage] = useState(""); // ✅ Add this line

  const initialValues = {
    email: "",
    password: "",
    confirmPassword: "",
    companyName: "",
    phoneNumber: "+91 ",
    firstName: "",
    lastName: "",
    terms: false,
  };

  const validationSchema = Yup.object({
    email: Yup.string()
      .matches(
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
        "Please enter a valid email address."
      )
      .required("Email ID is required."),
    password: Yup.string()
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,16}$/,
        "Password must meet complexity requirements."
      )
      .required("Password is required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm Password is required"),
    companyName: Yup.string()
      .max(21, "Company Name must not exceed 21 characters.")
      .matches(
        /^[a-zA-Z0-9\s\-_&.'@]+$/,
        "Company Name can only contain alphanumeric, spaces, hyphens, underscores, apostrophes, &, ., @."
      )
      .required("Company Name is required"),
    phoneNumber: Yup.string()
      .matches(/^\+91\s\d{10}$/, "Phone number must be 12 digits.")
      .required("Phone Number is required"),
    firstName: Yup.string()
      .max(50, "First Name must not exceed 50 characters.")
      .matches(/^[a-zA-Z ]+$/, "Please enter a valid first name.")
      .required("First name is required"),
    lastName: Yup.string()
      .max(50, "Last Name must not exceed 50 characters.")
      .matches(/^[a-zA-Z ]+$/, "Please enter a valid last name.")
      .required("Last Name is required"),
    terms: Yup.boolean().oneOf(
      [true],
      "You must accept the terms and conditions"
    ),
  });

  const handleSubmit = async (values) => {
    // Add 'async' here
    console.log("Form values:", values);

    const requestBody = {
      emailId: values.email, // ✅ Backend expects emailId
      phoneNumber: parseInt(
        values.phoneNumber.replace(/\D/g, "").slice(-10),
        10
      ), // ✅ Ensure number format
      firstName: values.firstName,
      lastName: values.lastName,
      password: values.password,
      companyName: values.companyName,
      role: values.role || "ADMIN", // Default role if not provided
    };
    localStorage.setItem("userEmail", values.email);
    localStorage.setItem("firstName", values.firstName);
    localStorage.setItem("lastName", values.lastName);
    localStorage.setItem("phoneNumber", values.phoneNumber.replace(/\D/g, "").slice(-10));
    localStorage.setItem("role", values.role || "ADMIN");
    localStorage.setItem("companyName", values.companyName);

    navigate("/orgDetails", { state: { signupData: requestBody } });
  };

  const handleClick = (tab) => {
    setActiveTab(tab);
    navigate(`/${tab}`); // Navigate to the respective route
  };

  return (
    <div className='signup-container'>
      <div className='signup-box'>
        <div className='tab-switch'>
          <button
            className={`tab ${activeTab === "signup" ? "active" : ""}`}
            onClick={() => handleClick("signup")}
          >
            Sign up
          </button>
          <button
            className={`tab ${activeTab === "signin" ? "active" : ""}`}
            onClick={() => handleClick("signin")}
          >
            Sign in
          </button>
        </div>

        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, setFieldValue }) => (
            <Form className='signup-form'>
              <div className='input-group'>
                <label>
                  Company Name<span className='required-asterisk'>*</span>
                </label>
                <Field name='companyName'>
                  {({ field, form }) => (
                    <input
                      {...field}
                      type='text'
                      placeholder='Enter your company name'
                      onChange={(e) => {
                        let value = e.target.value;

                        // Capitalize the first letter
                        if (value.length > 0) {
                          value =
                            value.charAt(0).toUpperCase() + value.slice(1);
                        }

                        // Check if value length exceeds 100 characters
                        if (value.length <= 21) {
                          form.setFieldValue("companyName", value);
                        }
                      }}
                    />
                  )}
                </Field>
                <ErrorMessage
                  name='companyName'
                  component='div'
                  className='error'
                />
              </div>

              <div className='input-group'>
                <label>
                  Email ID<span className='required-asterisk'>*</span>
                </label>
                <Field
                  type='email'
                  placeholder='Enter your email ID'
                  name='email'
                />
                <ErrorMessage name='email' component='div' className='error' />
              </div>

              <div className='input-group'>
                <label>
                  Phone Number<span className='required-asterisk'>*</span>
                </label>
                <Field
                  type='text'
                  name='phoneNumber'
                  onChange={(e) => {
                    let value = e.target.value;

                    // Automatically add +91 if not present
                    if (!value.startsWith("+91 ")) {
                      value = "+91 " + value.slice(4); // Ensure it starts with +91
                    }

                    // Restrict to 14 characters (including +91)
                    if (value.length > 12) {
                      value = value.slice(0, 14); // Limit the input to 14 characters
                    }

                    setFieldValue("phoneNumber", value); // Update field value
                  }}
                />
                <ErrorMessage
                  name='phoneNumber'
                  component='div'
                  className='error'
                />
              </div>

              <div className='input-row'>
                <div className='input-group'>
                  <label>
                    First Name<span className='required-asterisk'>*</span>
                  </label>
                  <Field name='firstName'>
                    {({ field, form }) => (
                      <input
                        {...field}
                        type='text'
                        placeholder='Enter your first name'
                        maxLength='50'
                        onChange={(e) => {
                          let value = e.target.value;
                          // Prevent more than 50 characters
                          if (value.length > 50) value = value.slice(0, 50);
                          // Capitalize the first letter
                          value = value.charAt(0).toUpperCase() + value.slice(1);
                          form.setFieldValue("firstName", value);
                        }}
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name='firstName'
                    component='div'
                    className='error'
                  />
                </div>
                <div className='input-group'>
                  <label>
                    Last Name<span className='required-asterisk'>*</span>
                  </label>
                  <Field name='lastName'>
                    {({ field, form }) => (
                      <input
                        {...field}
                        type='text'
                        placeholder='Enter your last name'
                        maxLength='50'
                        onChange={(e) => {
                          let value = e.target.value;
                          // Prevent more than 50 characters
                          if (value.length > 50) value = value.slice(0, 50);
                          // Capitalize the first letter
                          value = value.charAt(0).toUpperCase() + value.slice(1);
                          form.setFieldValue("lastName", value);
                        }}
                      />
                    )}
                  </Field>
                  <ErrorMessage
                    name='lastName'
                    component='div'
                    className='error'
                  />
                </div>
              </div>

              <div className='input-group'>
                <label>
                  Password<span className='required-asterisk'>*</span>
                </label>
                <div className='password-wrapper'>
                  <Field
                    type={showPassword ? "text" : "password"}
                    placeholder='Create your password'
                    name='password'
                  />
                  <span
                    className='toggle-password'
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <IoEyeOutline /> : <IoEyeOffOutline />}
                  </span>
                  <span
                    className='info-icon'
                    onClick={() => setShowPopup(!showPopup)}
                    title='Password Requirements'
                  >
                    <IoInformationCircleOutline />
                  </span>
                  {showPopup && (
                    <div className='password-popup'>
                      <p>
                        <strong>Password Requirement</strong>
                      </p>
                      <ul>
                        <li>8 to 16 characters</li>
                        <li>Uppercase</li>
                        <li>Lowercase</li>
                        <li>Number</li>
                        <li>Special character</li>
                      </ul>
                    </div>
                  )}
                </div>
                <ErrorMessage
                  name='password'
                  component='div'
                  className='error'
                />
              </div>

              <div className='input-group'>
                <label>
                  Confirm Password<span className='required-asterisk'>*</span>
                </label>
                <div className='password-wrapper'>
                  <Field
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder='Confirm your password'
                    name='confirmPassword'
                  />
                  <span
                    className='toggle-password'
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <IoEyeOutline />
                    ) : (
                      <IoEyeOffOutline />
                    )}
                  </span>
                </div>
                <ErrorMessage
                  name='confirmPassword'
                  component='div'
                  className='error'
                />
              </div>

              <div className='terms'>
                <Field type='checkbox' name='terms' />
                <span>
                  I accept the{" "}
                  <a
                    href='/policies'
                    target='_blank'
                    rel='noopener noreferrer'
                    style={{ color: "#78807aff", textDecoration: "none", fontWeight: "bold" }}
                  >
                    Terms & Conditions
                  </a>
                </span>
                <ErrorMessage name='terms' component='div' className='error' />
              </div>

              <button
                type='submit'
                disabled={isSubmitting}
                className='submit-button'
              >
                {isSubmitting ? "Submitting..." : "Sign Up"}
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default Signup;
